<template>
  <section>
    <section v-if="!edit">
      <div
        class="flex flex-col rounded-md border border-light-gray p-4 shadow-primary"
      >
        <div class="mb-8 flex justify-between">
          <p class="text-xl text-dark-gray">My Details</p>
          <div class="flex gap-3">
            <button-main
              class="text-1md"
              size="md"
              variant="secondary"
              @click="toggleEditOn()"
            >
              Edit Profile
            </button-main>
            <button-main
              class="text-1md"
              size="md"
              variant="secondary"
              @click="logOut()"
            >
              Log Out
            </button-main>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <p class="text-1xs font-bold uppercase text-dark-blue">Name</p>
            <p class="overflow-hidden text-ellipsis text-1md text-body-color">
              {{ full_name }}
            </p>
          </div>
          <div class="divider-line" />
          <template
            v-if="
              lastestInquiryStatus == 'approved' ||
              lastestInquiryStatus == 'declined'
            "
          >
            <div class="flex items-center justify-between">
              <p class="text-1xs font-bold uppercase text-dark-blue">
                Preferred name
              </p>
              <p class="overflow-hidden text-ellipsis text-1md text-body-color">
                {{ preferred_name }}
              </p>
            </div>
            <div class="divider-line" />
          </template>
          <div class="flex items-center justify-between">
            <div>
              <div
                v-if="verified"
                class="flex items-center gap-1"
              >
                <p class="text-1xs font-bold uppercase text-dark-blue">Email</p>
                <img
                  class="mb-1 h-4 w-4"
                  src="@/assets/yellow-checkmark.svg"
                  alt="certified icon"
                />
              </div>
              <p
                v-else
                class="text-1xs font-bold uppercase text-dark-blue"
              >
                Email (Unverified)
              </p>
            </div>
            <p
              class="overflow-hidden text-ellipsis text-1md"
              :class="
                !verified ? 'italic text-gray-over-dark' : 'text-body-color'
              "
            >
              {{ email }}
            </p>
          </div>
          <div class="divider-line" />
          <div class="flex items-center justify-between">
            <p class="text-1xs font-bold uppercase text-dark-blue">
              Company Name
            </p>
            <p class="overflow-hidden text-ellipsis text-1md text-body-color">
              {{ companyName }}
            </p>
          </div>
          <div
            v-if="latest_carrier_eld_provider?.integration?.id > 0"
            class="divider-line"
          />
          <div
            v-if="latest_carrier_eld_provider?.integration?.id > 0"
            class="flex items-center justify-between"
          >
            <p class="text-1xs font-bold uppercase text-dark-blue">
              ELD Provider
            </p>
            <div
              v-if="
                latest_carrier_eld_provider?.connect_channel?.logo?.url
                  ?.length > 0
              "
              class="relative h-auto w-48 px-3 py-2"
            >
              <img
                :src="latest_carrier_eld_provider?.connect_channel?.logo?.url"
                :alt="latest_carrier_eld_provider?.connect_channel?.name"
              />
            </div>
            <div
              v-else
              class="overflow-hidden text-ellipsis text-1md text-body-color"
            >
              {{ latest_carrier_eld_provider?.connect_channel?.name }}
            </div>
          </div>
        </div>
        <div class="flex md:justify-end">
          <div class="mt-4 w-full md:w-64">
            <button-main
              v-if="!verified"
              variant="quaternary"
              class="mt-2 w-20 text-1md md:w-7/12"
              @click="sendVerificationEmail"
            >
              Send Verification Email
            </button-main>
          </div>
        </div>
      </div>
      <div
        v-if="canViewOwnershipBlock"
        class="mt-12 flex flex-col rounded-md border border-light-gray p-4 shadow-primary"
      >
        <div class="flex items-center justify-between">
          <p class="text-xl text-dark-gray">Ownership</p>
          <ConfirmYourOwnership v-if="displayConfirmOwnershipButton" />
          <div v-else>
            <button-main
              class="text-1md"
              size="md"
              variant="secondary"
              @click="inviteOwnerModal = true"
            >
              Add an Owner
            </button-main>
          </div>
        </div>
        <RecognizedOwners
          :display-details="true"
          :recognized-owners="owners.recognized"
        />
        <InvitedOwners :invited-owners="owners.invited" />
      </div>

      <div
        v-if="canManageUsers && showRateConfirmationNotifications"
        class="mt-12"
      >
        <rate-confirmation-notifications></rate-confirmation-notifications>
      </div>
      <div
        v-if="canManageUsers && showContactSection"
        class="mt-12"
      >
        <contacts-section :is-loading="isLoading" />
      </div>
    </section>
    <div v-else>
      <edit-profile />
    </div>
    <IdentityVerification
      v-if="showIDWFlow"
      :prevent-redirects="true"
    />
    <PreferredNameModal />
    <ConfirmCompanyEmailModal />
    <add-owner-modal
      :visible="inviteOwnerModal"
      :loading="loadingInviteOwner"
      @success="handleInviteOwner"
      @close="inviteOwnerModal = false"
    />
  </section>
</template>

<script type="text/javascript">
import EditProfile from './EditProfile.vue';
import ProfileMixin from '@/mixins/profile';
import ButtonMain from '@/components/Button.vue';
import ConfirmYourOwnership from '@/components/ConfirmYourOwnership.vue';
import { toastResponse } from '@/common/handleServerResponse';
import InvitedOwners from '@/components/steps/Ownership/InvitedOwners.vue';
import RecognizedOwners from '@/components/steps/Ownership/RecognizedOwners.vue';
import PreferredNameModal from '@/components/modals/PreferredNameModal.vue';
import ConfirmCompanyEmailModal from '@/components/modals/ConfirmCompanyEmailModal.vue';
import IdentityVerification from '@/views/signup/IdentityVerification.vue';
import RateConfirmationNotifications from './RateConfirmationNotifications.vue';
import ContactsSection from '@/components/ContactsSection.vue';
import AddOwnerModal from '@/components/modals/AddOwnerModal.vue';

export default {
  name: 'ShowProfile',
  components: {
    ButtonMain,
    EditProfile,
    ConfirmYourOwnership,
    InvitedOwners,
    RecognizedOwners,
    PreferredNameModal,
    ConfirmCompanyEmailModal,
    IdentityVerification,
    RateConfirmationNotifications,
    AddOwnerModal,
    ContactsSection,
  },
  mixins: [ProfileMixin],
  data() {
    return {
      edit: false,
      inviteOwnerModal: false,
      loadingInviteOwner: false,
      isLoading: false,
    };
  },
  computed: {
    carrierId() {
      return (
        this.$store.state.users.currentUser?.company?.carrier_id ||
        this.$store.state.users.currentUser?.requested_carrier_id ||
        this.$store.state.carriers?.carrierData?.identity.id
      );
    },
    canViewOwnershipBlock() {
      const currentUserId = this.currentUser?.id;
      const currentUserEmail = this.currentUser?.email?.toLowerCase();

      const ownershipUsers = this.ownershipData?.ownership_users || [];

      const isRecognizedOwner = ownershipUsers.some(
        (owner) => owner.user_id === currentUserId
      );

      const isInvitedByEmail = ownershipUsers.some(
        (owner) =>
          owner.user_id === null &&
          owner.invited_user_email?.toLowerCase() === currentUserEmail
      );

      return isRecognizedOwner || isInvitedByEmail;
    },
    ownershipData() {
      return this.$store.state.ownership.ownershipData;
    },
    showIDWFlow() {
      if (this.$store.state.users.featureFlags?.disable_idv_ownership?.enabled)
        return false;

      if (this.$store.state.users.currentUser?.last_verified_identity_at)
        return false;

      // We want to force IDV here just when the user needs to confirm ownership.
      return this.displayConfirmOwnershipButton;
    },
    lastestInquiryStatus() {
      return this.$store.getters['users/inquiryStatus'];
    },
    ownershipUsers() {
      return this.$store.state.ownership.ownershipData?.ownership_users;
    },
    ownershipUser() {
      return (
        this.$store.state.ownership.ownershipData?.ownership_users?.find(
          (owner) => owner.user_id === this.$store.state.users.currentUser.id
        ) || undefined
      );
    },
    displayConfirmOwnershipButton() {
      return this.ownershipUser?.ssn_submitted_at === null;
    },
    userId() {
      return this.$store.state.users.currentUser?.id || '';
    },
    currentUser() {
      return this.$store.state.users?.currentUser;
    },
    canManageUsers() {
      return this.currentUser.scopes?.includes('manage_users');
    },
    showRateConfirmationNotifications() {
      return this.$store.state.users.featureFlags
        ?.rate_confirmation_email_notification?.enabled;
    },
    latest_carrier_eld_provider() {
      if (this.$store.state.onboardingPacket?.latest_carrier_eld_provider) {
        return this.$store.state.onboardingPacket?.latest_carrier_eld_provider;
      } else if (
        this.$store.state.carriers?.carrierData?.latest_carrier_eld_provider
      ) {
        return this.$store.state.carriers?.carrierData
          ?.latest_carrier_eld_provider;
      }
      return null;
    },
    owners() {
      const recognized =
        this.$store.state.ownership.ownershipData?.ownership_users?.filter(
          (owner) => owner?.status === 'complete' || owner?.status === 'started'
        ) || [];
      const invited =
        this.$store.state.ownership.ownershipData?.ownership_users?.filter(
          (owner) => owner?.status === 'invited'
        ) || [];

      return {
        recognized,
        invited,
      };
    },
    showContactSection() {
      return this.$store.state.users.featureFlags?.display_contact_section
        ?.enabled;
    },
  },
  created() {
    this.$store.dispatch('verifications/getEmailVerification');
    const storage = localStorage.getItem('redirect_url_after_sigin');
    if (storage) {
      let redirectAfterSignIn = JSON.parse(
        localStorage.getItem('redirect_url_after_sigin')
      );

      if (redirectAfterSignIn) {
        localStorage.removeItem('redirect_url_after_sigin');
        // return this.$router.push(redirectAfterSignIn);
      }
    }
    this.$store.dispatch(
      'onboardingPacket/getLatestEldProvider',
      this.currentUser?.company?.carrier_id
    );
  },
  async mounted() {
    if (this.showContactSection) {
      this.isLoading = true;
      await this.$store.dispatch('contacts/getContactsByCarrierId', {
        page: 1,
        perPage: 50,
        carrierId: this.carrierId,
      });
      this.isLoading = false;
    }
  },
  methods: {
    async ensureOwnershipCompanyExists() {
      const companyId = this.$store.state.users.currentUser.company.id;
      const ownership = await this.$store.dispatch(
        'ownership/getOwnershipByCompanyId',
        companyId
      );
      if (!ownership) {
        await this.$store.dispatch('ownership/createOwnershipCompany', {
          companyId,
        });
      }
    },
    async handleInviteOwner({ email }) {
      if (!email) {
        this.$toast?.open({
          message: 'Please provide an email for the owner',
          position: 'top-right',
          duration: 5000,
        });
        return;
      }
      try {
        this.loadingInviteOwner = true;
        if (!this.ownershipData) await this.ensureOwnershipCompanyExists();
        const { id: ownershipId } = this.ownershipData || {};

        await this.$store.dispatch('ownership/createOwnershipUser', {
          ownershipId,
          email: email?.toLowerCase(),
          type: 'owner',
        });

        await this.$store.dispatch(
          'ownership/getOwnershipByCompanyId',
          this.$store.state.users.currentUser.company.id
        );

        this.$toast?.open({
          message: 'Owner has been invited',
          position: 'top-right',
          duration: 5000,
        });
      } catch (error) {
        this.$toast?.open({
          message:
            error?.response?.data?.[0] ||
            'Something went wrong with your request. Please try again',
          position: 'top-right',
          duration: 5000,
        });
      } finally {
        this.loadingInviteOwner = false;
        this.inviteOwnerModal = false;
      }
    },
    toggleEditOn() {
      this.edit = true;
    },
    toggleEditOff() {
      this.edit = false;
    },
    async sendVerificationEmail() {
      const res = await this.$store.dispatch(
        'verifications/resendEmailVerification',
        this.userId
      );
      toastResponse(this, res, 'Email verification sent');
    },
    async logOut() {
      const logoutRes = await this.$store.dispatch('users/userSignOut');
      if (logoutRes) {
        this.$router.push({ name: 'SignUp' });
      }
    },
  },
};
</script>

<style>
.edit-button {
  @apply cursor-pointer rounded-md border border-gray-over-dark bg-white text-gray-over-dark;
  padding: 8.5px 14px !important;
}

.save-button {
  @apply cursor-pointer rounded-md bg-yellow-500 p-4 text-base text-white;
  padding: 8.5px 14px !important;
}

.divider-line {
  height: 1px;
  @apply rounded bg-light-gray;
}
</style>
